import type Customer from './Customer'
import {
  isNonCarrierRole,
  joinPlaceNames,
  resolveContractualShipperIdForRequest,
  ShareDocumentToDriverBy,
  type ContractualShipper,
  type OperatorRole,
  type PlaceRef
} from './DeliveryNote'
import type Driver from './Driver'
import type Employee from './Employee'
import type Place from './Place'
import type Vehicle from './Vehicle'

export type SchemaVersion = 1 | 2

export const SCHEMA_VERSION_V2: SchemaVersion = 2

export interface PassengerTransportDocumentDelivery {
  id?: string
  origins?: Place[]
  destinations?: Place[]
}

export interface PassengerTransportDocumentDeliveryDto {
  id?: string
  origins: PlaceRef[]
  destinations: PlaceRef[]
}

export function isPassengerTransportDocumentV2(
  document: Pick<PassengerTransportDocument, 'schemaVersion'>
): boolean {
  return document.schemaVersion === 2
}

export function createEmptyPassengerTransportDocumentDeliveryDto(): PassengerTransportDocumentDeliveryDto {
  return {
    origins: [],
    destinations: []
  }
}

export function getPassengerTransportDocumentListOriginLabel(
  document: PassengerTransportDocument
): string {
  if (isPassengerTransportDocumentV2(document) && document.deliveries?.length) {
    return joinPlaceNames(document.deliveries[0].origins)
  }
  return document.origin?.name ?? ''
}

export function getPassengerTransportDocumentListDestinationLabel(
  document: PassengerTransportDocument
): string {
  if (isPassengerTransportDocumentV2(document) && document.deliveries?.length) {
    return joinPlaceNames(document.deliveries[0].destinations)
  }
  return document.destination?.name ?? ''
}

export type PassengerTransportDocumentType =
  | 'DISCRETIONARY'
  | 'TURISTIC'
  | 'DISCRETIONARY_REGULAR_PUBLIC_REINFORCEMENT'
  | 'DISCRETIONARY_REGULAR_SPECIAL_COLLABORATION'

export const PASSENGER_TRANSPORT_DOCUMENT_TYPES: PassengerTransportDocumentType[] = [
  'DISCRETIONARY',
  'TURISTIC',
  'DISCRETIONARY_REGULAR_PUBLIC_REINFORCEMENT',
  'DISCRETIONARY_REGULAR_SPECIAL_COLLABORATION'
]

export function passengerTransportDocumentTypeRequiresDescription(
  type: PassengerTransportDocumentType | undefined
): boolean {
  return (
    type === 'DISCRETIONARY_REGULAR_PUBLIC_REINFORCEMENT' ||
    type === 'DISCRETIONARY_REGULAR_SPECIAL_COLLABORATION'
  )
}

export interface PassengerTransportDocumentBill {
  id: string
  number?: string
}

export interface PassengerTransportDocument {
  id: string
  companyId: string
  schemaVersion?: SchemaVersion
  deliveries?: PassengerTransportDocumentDelivery[]
  number: string
  autogenerateNumber?: boolean
  date: string
  type: PassengerTransportDocumentType
  description?: string
  customer?: Customer
  employee?: Employee
  employeeName?: string
  driver?: Driver
  carrier?: Customer
  contractualShipper?: ContractualShipper
  operatorRole?: OperatorRole
  vehicles?: Vehicle[]
  vehiclePlates?: string[]
  origin?: Place
  destination?: Place
  notes?: string
  bill?: PassengerTransportDocumentBill
  imageNames?: string[]
  manualValidationRequired?: boolean
  externalQrCode?: string
  editUrl?: string
  qrUrl?: string
  creationDate?: string
  modificationDate?: string
  shareDocumentToDriverBy?: ShareDocumentToDriverBy
  hideClient?: boolean
}

export interface PassengerTransportDocumentRequest {
  id?: string
  schemaVersion?: SchemaVersion
  deliveries?: PassengerTransportDocumentDeliveryDto[]
  employeeId?: string
  employeeNif?: string
  driverId?: string
  number?: string
  autogenerateNumber?: boolean
  type: PassengerTransportDocumentType
  description?: string
  customerId?: string
  operatorRole?: OperatorRole
  carrierId?: string
  carrierName?: string
  carrierVatNumber?: string
  driverName?: string
  driverSurname?: string
  driverNif?: string
  driverEmail?: string
  customerName?: string
  customerVatNumber?: string
  customerAddress?: string
  customerEmail?: string
  contractualShipperId?: string
  contractualShipperBusinessName?: string
  contractualShipperVatNumber?: string
  contractualShipperAddress?: string
  contractualShipperPostCode?: string
  contractualShipperCity?: string
  contractualShipperState?: string
  contractualShipperCountry?: string
  vehicleIds?: string[]
  vehiclePlates?: string[]
  originId?: string
  originName?: string
  destinationId?: string
  destinationName?: string
  notes?: string
  externalQrCode?: string
  date?: string
  shareDocumentToDriverBy?: ShareDocumentToDriverBy
  hideClient?: boolean
}

export function hasExternalQrCode(
  document: Pick<PassengerTransportDocument, 'externalQrCode'>
): boolean {
  return !!document.externalQrCode?.trim()
}

export function isPassengerTransportDocumentBilled(
  document: Pick<PassengerTransportDocument, 'bill'>
): boolean {
  return document.bill !== undefined && document.bill !== null
}

export function isPassengerTransportDocumentUncompleted(
  document: Pick<
    PassengerTransportDocument,
    'customer' | 'origin' | 'destination' | 'schemaVersion' | 'deliveries'
  >
): boolean {
  if (!document.customer) {
    return true
  }
  if (isPassengerTransportDocumentV2(document) && document.deliveries?.length) {
    const hasRoute = document.deliveries.some(
      (delivery) =>
        (delivery.origins?.length ?? 0) > 0 && (delivery.destinations?.length ?? 0) > 0
    )
    return !hasRoute
  }
  return !document.origin || !document.destination
}

const freeTextField = <T extends { id?: string }>(
  entity: T | undefined,
  field: keyof T
): string | undefined => {
  if (!entity || entity.id) {
    return undefined
  }
  const value = entity[field]
  return typeof value === 'string' ? value : undefined
}

export const mapPassengerTransportDocumentEmbeddedToRequest = (
  document: PassengerTransportDocument
) => ({
  customerName: freeTextField(document.customer, 'name'),
  customerVatNumber: freeTextField(document.customer, 'vatNumber'),
  customerAddress: freeTextField(document.customer, 'address'),
  customerEmail: freeTextField(document.customer, 'email'),
  carrierName: freeTextField(document.carrier, 'name'),
  carrierVatNumber: freeTextField(document.carrier, 'vatNumber'),
  contractualShipperBusinessName: freeTextField(document.contractualShipper, 'businessName'),
  contractualShipperVatNumber: freeTextField(document.contractualShipper, 'vatNumber'),
  contractualShipperAddress: freeTextField(document.contractualShipper, 'address'),
  contractualShipperPostCode: freeTextField(document.contractualShipper, 'postCode'),
  contractualShipperCity: freeTextField(document.contractualShipper, 'city'),
  contractualShipperState: freeTextField(document.contractualShipper, 'state'),
  contractualShipperCountry: freeTextField(document.contractualShipper, 'country'),
  originName: freeTextField(document.origin, 'name'),
  destinationName: freeTextField(document.destination, 'name')
})

export const mapPassengerTransportDocumentToRequest = (
  document: PassengerTransportDocument,
  customers: Array<{ id: string }> = []
): PassengerTransportDocumentRequest => {
  const operator = isNonCarrierRole(document.operatorRole)
  if (isPassengerTransportDocumentV2(document) && document.deliveries?.length) {
    return {
      id: document.id,
      employeeId: operator ? undefined : document.employee?.id,
      employeeNif: operator
        ? undefined
        : document.employee && !document.employee.id
          ? document.employee.nif
          : undefined,
      driverId: operator ? document.driver?.id : undefined,
      driverName: operator && document.driver && !document.driver.id
        ? document.driver.name
        : undefined,
      driverSurname: operator && document.driver && !document.driver.id
        ? document.driver.surname
        : undefined,
      driverNif: operator && document.driver && !document.driver.id
        ? document.driver.nif
        : undefined,
      driverEmail: operator && document.driver && !document.driver.id
        ? document.driver.email
        : undefined,
      number: document.number,
      date: document.date,
      type: document.type,
      description: document.description,
      schemaVersion: SCHEMA_VERSION_V2,
      deliveries: document.deliveries.map((delivery) => ({
        id: delivery.id,
        origins: (delivery.origins ?? []).map((place) => ({
          id: place.id ?? undefined,
          name: place.name ?? undefined
        })),
        destinations: (delivery.destinations ?? []).map((place) => ({
          id: place.id ?? undefined,
          name: place.name ?? undefined
        }))
      })),
      customerId: document.customer?.id,
      ...mapPassengerTransportDocumentEmbeddedToRequest(document),
      vehicleIds: document.vehicles?.length ? document.vehicles.map((vehicle) => vehicle.id) : [],
      vehiclePlates: document.vehiclePlates?.length ? [...document.vehiclePlates] : undefined,
      notes: document.notes,
      autogenerateNumber: document.autogenerateNumber,
      operatorRole: document.operatorRole,
      carrierId: document.carrier?.id,
      contractualShipperId: resolveContractualShipperIdForRequest(document, customers),
      hideClient: document.hideClient ?? false,
      shareDocumentToDriverBy: operator
        ? (document.shareDocumentToDriverBy ?? ShareDocumentToDriverBy.NONE)
        : undefined
    }
  }

  return {
    id: document.id,
    employeeId: operator ? undefined : document.employee?.id,
    employeeNif: operator
      ? undefined
      : document.employee && !document.employee.id
        ? document.employee.nif
        : undefined,
    driverId: operator ? document.driver?.id : undefined,
    driverName: operator && document.driver && !document.driver.id
      ? document.driver.name
      : undefined,
    driverSurname: operator && document.driver && !document.driver.id
      ? document.driver.surname
      : undefined,
    driverNif: operator && document.driver && !document.driver.id
      ? document.driver.nif
      : undefined,
    driverEmail: operator && document.driver && !document.driver.id
      ? document.driver.email
      : undefined,
    number: document.number,
    date: document.date,
    type: document.type,
    description: document.description,
    customerId: document.customer?.id,
    ...mapPassengerTransportDocumentEmbeddedToRequest(document),
    vehicleIds: document.vehicles?.length ? document.vehicles.map((vehicle) => vehicle.id) : [],
    vehiclePlates: document.vehiclePlates?.length ? [...document.vehiclePlates] : undefined,
    originId: document.origin?.id,
    destinationId: document.destination?.id,
    notes: document.notes,
    autogenerateNumber: document.autogenerateNumber,
    operatorRole: document.operatorRole,
    carrierId: document.carrier?.id,
    contractualShipperId: resolveContractualShipperIdForRequest(document, customers),
    hideClient: document.hideClient ?? false,
    shareDocumentToDriverBy: operator
      ? (document.shareDocumentToDriverBy ?? ShareDocumentToDriverBy.NONE)
      : undefined
  }
}

function mapPassengerTransportDocumentToCopyDeliveries(
  document: PassengerTransportDocument
): PassengerTransportDocumentDeliveryDto[] {
  if (isPassengerTransportDocumentV2(document) && document.deliveries?.length) {
    return document.deliveries.map((delivery) => ({
      origins: (delivery.origins ?? []).map((place) => ({
        id: place.id ?? undefined,
        name: place.name ?? undefined,
      })),
      destinations: (delivery.destinations ?? []).map((place) => ({
        id: place.id ?? undefined,
        name: place.name ?? undefined,
      })),
    }))
  }

  return [{
    origins: [{
      id: document.origin?.id,
      name: document.origin?.id ? undefined : document.origin?.name,
    }],
    destinations: [{
      id: document.destination?.id,
      name: document.destination?.id ? undefined : document.destination?.name,
    }],
  }]
}

export const mapPassengerTransportDocumentToCopyRequest = (
  document: PassengerTransportDocument,
  customers: Array<{ id: string }> = [],
  options?: { v2SchemaEnabled?: boolean }
): PassengerTransportDocumentRequest => {
  const v2SchemaEnabled = options?.v2SchemaEnabled ?? true
  const useFirstDelivery =
    isPassengerTransportDocumentV2(document) && !!document.deliveries?.length
  const firstDelivery = useFirstDelivery ? document.deliveries![0] : undefined
  const copyBase: PassengerTransportDocumentRequest = {
    ...mapPassengerTransportDocumentToRequest(document, customers),
    id: undefined,
    number: '',
    autogenerateNumber: document.autogenerateNumber ?? true,
  }

  if (!v2SchemaEnabled) {
    const { schemaVersion: _schemaVersion, deliveries: _deliveries, ...legacy } = copyBase
    return {
      ...legacy,
      originId: useFirstDelivery ? firstDelivery!.origins?.[0]?.id : document.origin?.id,
      destinationId: useFirstDelivery
        ? firstDelivery!.destinations?.[0]?.id
        : document.destination?.id,
    }
  }

  const { deliveries: _deliveries, schemaVersion: _schemaVersion, ...withoutDeliveries } = copyBase
  return {
    ...withoutDeliveries,
    schemaVersion: SCHEMA_VERSION_V2,
    deliveries: mapPassengerTransportDocumentToCopyDeliveries(document),
    originId: useFirstDelivery ? firstDelivery!.origins?.[0]?.id : document.origin?.id,
    destinationId: useFirstDelivery
      ? firstDelivery!.destinations?.[0]?.id
      : document.destination?.id,
  }
}

export const getPassengerTransportDocumentEmployeeDisplayName = (
  document: PassengerTransportDocument
): string => {
  if (isNonCarrierRole(document.operatorRole)) {
    return getPassengerTransportDocumentDriverDisplayName(document)
  }
  if (document.employee) {
    return `${document.employee.name} ${document.employee.surname ?? ''}`.trim()
  }
  return document.employeeName ?? ''
}

export const getPassengerTransportDocumentDriverDisplayName = (
  document: PassengerTransportDocument
): string => {
  if (!document.driver) {
    return ''
  }
  return `${document.driver.name} ${document.driver.surname ?? ''}`.trim()
}

export const getPassengerTransportDocumentVehiclesDisplayText = (
  document: PassengerTransportDocument
): string => {
  if (document.vehicles?.length) {
    return document.vehicles.map((vehicle) => vehicle.plate).join(', ')
  }
  return document.vehiclePlates?.join(', ') ?? ''
}
